import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './_Projects.scss';

function ProjectLinks({ links, title }) {
  if (!links) return null;

  return (
    <div className="editorial-card-links">
      {links.preview && (
        <a
          href={links.preview}
          rel="noopener noreferrer"
          target="_blank"
          className="editorial-link primary"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Open live demo for ${title}`}
        >
          Live Demo <FontAwesomeIcon icon="external-link-alt" aria-hidden="true" />
        </a>
      )}
      {links.github && (
        <a
          href={links.github}
          rel="noopener noreferrer"
          target="_blank"
          className="editorial-link secondary"
          onClick={(e) => e.stopPropagation()}
          aria-label={`View GitHub repository for ${title}`}
        >
          <FontAwesomeIcon icon={['fab', 'github']} aria-hidden="true" /> GitHub
        </a>
      )}
    </div>
  );
}

function Projects({ projects }) {
  const projectList = useMemo(() => (Array.isArray(projects) ? projects : []), [projects]);
  const [expandedIndex, setExpandedIndex] = useState(null);

  if (!projectList.length) {
    return <div className="projects-container empty">No projects to show yet.</div>;
  }

  const toggleExpand = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section className="projects-container" aria-label="Featured Projects">
      {/* Unified Editorial Section Header Bar */}
      <div className="editorial-header">
        <h3 className="editorial-header-title">Featured Projects</h3>
        <a
          href="https://github.com/Xin1337"
          target="_blank"
          rel="noopener noreferrer"
          className="editorial-header-btn"
          aria-label="View all GitHub repositories by Dominic"
        >
          SEE ALL REPOS ↗
        </a>
      </div>

      <div className="editorial-grid">
        {projectList.map((project, index) => {
          const isExpanded = expandedIndex === index;
          const detailsId = `project-details-${index}`;

          return (
            <article
              key={project.title}
              className={`editorial-card ${isExpanded ? 'is-expanded' : ''}`}
              onClick={() => toggleExpand(index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleExpand(index);
                }
              }}
              tabIndex={0}
              role="region"
              aria-label={`${project.title} project card`}
            >
              {/* Unified Brand Color Frame around Media */}
              <div className="editorial-card-frame">
                {project.image ? (
                  <img
                    src={project.image}
                    alt={project.alt || `${project.title} project preview screenshot`}
                  />
                ) : (
                  <div className="editorial-card-placeholder">{project.title}</div>
                )}
              </div>

              {/* Micro-badge Bar */}
              <div className="editorial-badge-bar">
                <span className="editorial-badge">FEATURED PROJECT</span>
                <span className="editorial-badge-sub">0{index + 1}</span>
              </div>

              {/* Card Main Info */}
              <div className="editorial-card-body">
                <h3 className="editorial-card-title">{project.title}</h3>
                <p className="editorial-card-teaser">{project.description}</p>
                <div className="editorial-card-byline">
                  By Dominic • {Array.isArray(project.skills) ? project.skills.join(' · ') : ''}
                </div>
              </div>

              {/* Pure CSS spring-animated height expansion */}
              <div className="editorial-card-expandable" id={detailsId}>
                <div className="editorial-card-expandable-inner">
                  <div className="editorial-card-secondary">
                    <p className="editorial-card-detail-text">
                      Click below to view the interactive live application or inspect source code on GitHub.
                    </p>
                    <ProjectLinks links={project.links} title={project.title} />
                  </div>
                </div>
              </div>

              {/* Semantic Button for Expand Toggle with ARIA Attributes */}
              <button
                type="button"
                className="editorial-card-toggle-btn"
                aria-expanded={isExpanded}
                aria-controls={detailsId}
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${project.title}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(index);
                }}
              >
                {isExpanded ? 'Show less ▲' : 'Tap to expand details ▼'}
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default Projects;
